import { getApiEndpoint } from '../config/api.js';
import type { CalendarEvent, Highlight, HighlightInsight } from '@akem/shared';

export type HighlightWithInsight = Highlight & { insight?: HighlightInsight };

// Assuming the backend types are similar enough or we'll map them.
// For a cleaner setup, these types could be shared from a `packages/shared` or directly from backend's types.
export interface StockMarketData {
  symbol: string;
  price: number;
  change: number;
  changePercent: number;
  volume: number;
  // Add high, low if the backend provides them and widget needs them
}

export interface MarketDataResponse {
  data?: StockMarketData[];
  error?: string;
}

export const marketService = {
  async getMarketDataForSymbols(symbols: string[]): Promise<StockMarketData[]> {
    if (symbols.length === 0) {
      return [];
    }
    
    const params = new URLSearchParams({
      symbols: symbols.join(',')
    });
    
    const response = await fetch(`${getApiEndpoint()}/marketData?${params}`);
    
    if (!response.ok) {
      throw new Error(`Market data API error: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json() as MarketDataResponse;
    
    if (data.error) {
      throw new Error(data.error);
    }
    if (!data.data) {
      throw new Error('No data received from market data API');
    }
    
    return data.data;
  },

  async getMarketCalendar(params?: { from?: string; to?: string }): Promise<CalendarEvent[]> {
    const url = new URL(`${getApiEndpoint()}/market/calendar`);
    if (params?.from) url.searchParams.set('from', params.from);
    if (params?.to) url.searchParams.set('to', params.to);
    const res = await fetch(url.toString());
    if (!res.ok) throw new Error(`Market calendar API error: ${res.status}`);
    const json = await res.json();
    return (json?.events as CalendarEvent[]) || [];
  },

  async getMarketHighlights(): Promise<HighlightWithInsight[]> {
    const res = await fetch(`${getApiEndpoint()}/market/impact/highlights`);
    if (!res.ok) throw new Error(`Market highlights API error: ${res.status}`);
    const json = await res.json();
    return (json?.highlights as HighlightWithInsight[]) || [];
  },

  async getIndices(): Promise<Array<{ id: string; name: string; symbol: string; level: number; changePercent: number; intradaySparkline: number[] }>> {
    const res = await fetch(`${getApiEndpoint()}/market/indices`);
    if (!res.ok) throw new Error(`Market indices API error: ${res.status}`);
    const json = await res.json();
    return (json?.indices as any[]) || [];
  },

  async getSectors(): Promise<Array<{ id: string; name: string; changePercent: number; leaders: string[]; laggards: string[] }>> {
    const res = await fetch(`${getApiEndpoint()}/market/sectors`);
    if (!res.ok) throw new Error(`Market sectors API error: ${res.status}`);
    const json = await res.json();
    return (json?.sectors as any[]) || [];
  },

  async getPortfolioOverviews(): Promise<Array<{ id: string; name: string; totalValue: number; changePercent: number; holdings: Array<{ symbol: string; weight: number; changePercent: number }> }>> {
    const res = await fetch(`${getApiEndpoint()}/market/portfolio/overviews`);
    if (!res.ok) throw new Error(`Market portfolio API error: ${res.status}`);
    const json = await res.json();
    return (json?.portfolios as any[]) || [];
  },
};
